"use client"

import { useEffect, useState } from "react"
import { FormGroup, Label, Input, Button, Alert, Spinner } from "reactstrap"
import { Camera, Edit3, Save, X, Lock, User, Mail, Phone } from "react-feather"
import styles from "./UserInfo.module.scss"
import { updateMerchantDetails, updateMerchantPassword } from "../../../../api-handle/merchantApi"
import { uploadSingleFile } from "../../../../api-handle/fileApi"

const FILE_BASE_URL = (process.env.REACT_APP_FILE_BASE_URL || process.env.REACT_APP_BASE_URL || "http://localhost:4001").replace(/\/?$/, "/")

const UserInfo = ({ userData, setUserData, handleUpdate, refetch, setRefetch }) => {
    const [editMode, setEditMode] = useState(false)
    const [updatedData, setUpdatedData] = useState(userData)
    const [uploading, setUploading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")
    const [selectedFile, setSelectedFile] = useState(null)
    const [previewUrl, setPreviewUrl] = useState(null)

    // Password states
    const [oldPassword, setOldPassword] = useState("")
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [passwordError, setPasswordError] = useState("")
    const [passwordSuccess, setPasswordSuccess] = useState("")
    const [updatingPassword, setUpdatingPassword] = useState(false)

    const handlePasswordUpdate = async () => {
        setPasswordError("")
        setPasswordSuccess("")
        setUpdatingPassword(true)

        // Validation: Check empty fields
        if (!oldPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
            setPasswordError("All fields are required.")
            setUpdatingPassword(false)
            return
        }

        if (newPassword.length < 8 || newPassword.length > 16) {
            setPasswordError("Password must be between 8 and 16 characters.")
            setUpdatingPassword(false)
            return
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/
        if (!passwordRegex.test(newPassword)) {
            setPasswordError("Password must include at least 1 uppercase, 1 lowercase, 1 number, and 1 special character.")
            setUpdatingPassword(false)
            return
        }

        // Validation: Check if new passwords match
        if (newPassword !== confirmPassword) {
            setPasswordError("New password and Confirm password do not match.")
            setUpdatingPassword(false)
            return
        }

        if (newPassword === oldPassword) {
            setPasswordError("New password and Old password should not be same.")
            setUpdatingPassword(false)
            return
        }

        try {
            await updateMerchantPassword({ oldPassword, newPassword })
            setPasswordSuccess("Password updated successfully!")
            // Clear inputs after successful update
            setOldPassword("")
            setNewPassword("")
            setConfirmPassword("")
        } catch (error) {
            setPasswordError(error?.message || "Failed to update password.")
        } finally {
            setUpdatingPassword(false)
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target
        setUpdatedData((prev) => ({ ...prev, [name]: value }))
        setError("")
    }

    const handleProfileImageChange = (e) => {
        setError("")
        const file = e.target.files[0]
        if (!file) return

        // Validate file type
        if (!file.type.startsWith("image/")) {
            setError("Please select a valid image file.")
            return
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
            setError("Image size should be less than 5MB.")
            return
        }

        // Store file and create preview
        setSelectedFile(file)
        const reader = new FileReader()
        reader.onload = (e) => {
            setPreviewUrl(e.target.result)
        }
        reader.readAsDataURL(file)
        setSuccess("Image selected. Click 'Save Changes' to upload.")
    }

    const handleSave = async () => {
        setSaving(true)
        setError("")

        try {
            const finalUpdatedData = { ...updatedData }

            // Upload image if a new file is selected
            if (selectedFile) {
                setUploading(true)
                try {
                    const uploadResponse = await uploadSingleFile(selectedFile)
                    // uploadResponse is an object, we only need the fileName
                    finalUpdatedData.profilePhoto = uploadResponse.fileName
                } catch (uploadError) {
                    setError("Failed to upload image. Please try again.")
                    setSaving(false)
                    setUploading(false)
                    return
                }
                setUploading(false)
            }

            // Update merchant details with the final data (including new fileName if uploaded)
            await updateMerchantDetails({ userData: finalUpdatedData })
            setUserData(finalUpdatedData)
            setEditMode(false)
            setSelectedFile(null)
            setPreviewUrl(null)
            setSuccess("Profile updated successfully!")
            setRefetch(!refetch)
        } catch (error) {
            setError("Error updating merchant details.")
        } finally {
            setSaving(false)
        }
    }

    const handleCancel = () => {
        setUpdatedData(userData)
        setEditMode(false)
        setError("")
        setSuccess("")
        setSelectedFile(null)
        setPreviewUrl(null)
    }

    useEffect(() => {
        setUpdatedData(userData)
    }, [userData])

    // Clear messages after 5 seconds
    useEffect(() => {
        if (success) {
            const timer = setTimeout(() => setSuccess(""), 5000)
            return () => clearTimeout(timer)
        }
    }, [success])

    useEffect(() => {
        if (passwordSuccess) {
            const timer = setTimeout(() => setPasswordSuccess(""), 5000)
            return () => clearTimeout(timer)
        }
    }, [passwordSuccess])

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                {/* Header */}
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <User size={24} />
                        Profile Information
                    </h2>
                </div>

                {/* Alerts */}
                {error && (
                    <Alert color="danger" className={styles.alert}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert color="success" className={styles.alert}>
                        {success}
                    </Alert>
                )}

                <div className={styles.cardBody}>
                    {/* Profile Image Section */}
                    <div className={styles.profileSection}>
                        <div className={styles.profileWrapper}>
                            <div className={styles.profileImageContainer}>
                                <img
                                    src={
                                        previewUrl ||
                                        (updatedData.profilePhoto
                                            ? `${FILE_BASE_URL}${updatedData.profilePhoto}`
                                            : `https://api.dicebear.com/7.x/initials/svg?seed=${userData.name}`)
                                    }
                                    alt="Profile"
                                    className={styles.profileImage}
                                />
                                {uploading && (
                                    <div className={styles.uploadingOverlay}>
                                        <Spinner size="sm" color="white" />
                                    </div>
                                )}
                            </div>
                            {editMode && (
                                <label className={styles.uploadIcon}>
                                    <Camera size={16} />
                                    <input type="file" accept="image/*" onChange={handleProfileImageChange} disabled={uploading} />
                                </label>
                            )}
                        </div>
                        <div className={styles.profileInfo}>
                            <h4 className={styles.userName}>{updatedData.name}</h4>
                            <p className={styles.userEmail}>{updatedData.email}</p>
                        </div>
                    </div>

                    {/* User Fields */}
                    <div className={styles.userInfo}>
                        <div className={styles.formRow}>
                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>
                                    <User size={16} />
                                    Full Name
                                </Label>
                                <Input
                                    type="text"
                                    name="name"
                                    value={updatedData.name || ""}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    className={styles.input}
                                    placeholder="Enter your full name"
                                />
                            </FormGroup>

                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>
                                    <Mail size={16} />
                                    Email Address
                                </Label>
                                <Input
                                    type="email"
                                    name="email"
                                    value={updatedData.email || ""}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    className={styles.input}
                                    placeholder="Enter your email address"
                                />
                            </FormGroup>

                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>
                                    <Phone size={16} />
                                    Phone Number
                                </Label>
                                <Input
                                    type="text"
                                    name="phone"
                                    value={updatedData.phone || ""}
                                    onChange={handleChange}
                                    disabled={!editMode}
                                    className={styles.input}
                                    placeholder="Enter your phone number"
                                />
                            </FormGroup>
                        </div>

                        {/* Action Buttons */}
                        <div className={styles.buttonGroup}>
                            {editMode ? (
                                <>
                                    <Button
                                        color="success"
                                        onClick={handleSave}
                                        disabled={saving || uploading}
                                        className={styles.saveBtn}
                                    >
                                        {saving ? (
                                            uploading ? (
                                                <>
                                                    <Spinner size="sm" />
                                                    Uploading Image...
                                                </>
                                            ) : (
                                                <>
                                                    <Spinner size="sm" />
                                                    Saving...
                                                </>
                                            )
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                Save Changes
                                            </>
                                        )}
                                    </Button>
                                    <Button color="secondary" onClick={handleCancel} className={styles.cancelBtn} disabled={saving}>
                                        <X size={16} />
                                        Cancel
                                    </Button>
                                </>
                            ) : (
                                <Button color="primary" onClick={() => setEditMode(true)} className={styles.editBtn}>
                                    <Edit3 size={16} />
                                    Edit Profile
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Password Section */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h3 className={styles.cardTitle}>
                        <Lock size={20} />
                        Change Password
                    </h3>
                </div>

                {/* Password Alerts */}
                {passwordError && (
                    <Alert color="danger" className={styles.alert}>
                        {passwordError}
                    </Alert>
                )}
                {passwordSuccess && (
                    <Alert color="success" className={styles.alert}>
                        {passwordSuccess}
                    </Alert>
                )}

                <div className={styles.cardBody}>
                    <div className={styles.passwordForm}>
                        <div className={styles.formRow}>
                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>Current Password</Label>
                                <Input
                                    type="password"
                                    placeholder="Enter current password"
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    className={styles.input}
                                />
                            </FormGroup>

                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>New Password</Label>
                                <Input
                                    type="password"
                                    placeholder="Enter new password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className={styles.input}
                                />
                            </FormGroup>

                            <FormGroup className={styles.formGroup}>
                                <Label className={styles.label}>Confirm New Password</Label>
                                <Input
                                    type="password"
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className={styles.input}
                                />
                            </FormGroup>
                        </div>

                        <div className={styles.passwordRequirements}>
                            <small className={styles.requirementText}>
                                Password must be 8-16 characters with at least 1 uppercase, 1 lowercase, 1 number, and 1 special
                                character.
                            </small>
                        </div>

                        <div className={styles.passwordButtonGroup}>
                            <Button
                                color="success"
                                onClick={handlePasswordUpdate}
                                disabled={updatingPassword}
                                className={styles.updatePasswordBtn}
                            >
                                {updatingPassword ? <Spinner size="sm" /> : <Lock size={16} />}
                                {updatingPassword ? "Updating..." : "Update Password"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default UserInfo
